import { msg } from "@lingui/core/macro";
import { createMemo, For, Show } from "solid-js";
import { Mail, Pencil, Plus, Trash2 } from "@fleet/icons";
import { Button } from "@/components/ui/button";
import { Badge, Skeleton } from "@fleet/ui";
import { NotFoundState } from "@/components/ui/not-found-state";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import type { components } from "@malla/shared";
import { useI18n } from "@/providers/i18n-provider";
import { EmailTemplateEditor } from "./email-template-editor";
import { QueryErrorState } from "@/components/ui/query-error-state";

type EmailTemplate = components["schemas"]["EmailTemplate"];

interface EmailTemplateManagerProps {
  /**
   * Editor mode, derived from the URL (campaign #15b — addressable):
   *   - `"new"`  → `/admin/email/new`        (create template)
   *   - `"edit"` → `/admin/email/$templateId` (edit; `editId` is the ID)
   *   - absent  → `/admin/email`            (list only)
   */
  mode?: () => "new" | "edit" | undefined;
  /** ID of the template to edit (when `mode() === "edit"`). */
  editId?: () => string | undefined;
  /** Closes the editor by navigating back to `/admin/email`. */
  onClose?: () => void;
  /** Opens the create editor by navigating to `/admin/email/new`. */
  onNew?: () => void;
  /** Opens the edit editor by navigating to `/admin/email/$templateId`. */
  onEdit?: (id: string) => void;
}

/**
 * Email template CRUD (campaign #11). Lists templates (system +
 * custom), and allows creating/editing/deleting. System templates cannot be deleted.
 * The create/edit editor is addressable by URL (#15b): `/admin/email/new`
 * and `/admin/email/$templateId`.
 */
export function EmailTemplateManager(props: EmailTemplateManagerProps = {}) {
  const { t } = useI18n();

  const templatesQuery = createApiQuery<EmailTemplate[], components["schemas"]["ErrorModel"]>(
    () => ["admin", "email", "templates"],
    async () => api.GET("/api/admin/email/templates"),
    () => ({ staleTime: 30_000 }),
  );

  const deleteMutation = createApiMutation(async (vars: { id: string }) =>
    api.POST("/api/admin/email/templates/delete", { body: vars }),
  );

  // Editor state derived from the URL (not local signals): copying/pasting the
  // URL reopens the same editor on another device.
  const isCreating = createMemo(() => props.mode?.() === "new");
  const isEditing = createMemo(() => props.mode?.() === "edit");
  const editing = createMemo<EmailTemplate | null>(() => {
    if (!isEditing()) {
      return null;
    }
    const id = props.editId?.();
    return templatesQuery.data?.find((tpl) => tpl.id === id) ?? null;
  });
  // The template to edit does not exist (invalid or deleted ID) once the list has loaded.
  const editNotFound = createMemo(
    () => isEditing() && !templatesQuery.isLoading && editing() == null,
  );

  const handleDelete = async (tpl: EmailTemplate) => {
    if (tpl.isSystem) {
      return;
    }
    try {
      await deleteMutation.mutateAsync({ id: tpl.id });
      await templatesQuery.refetch();
    } catch (err) {
      console.error("Error deleting email template:", err);
    }
  };

  const onSaved = async () => {
    await templatesQuery.refetch();
    props.onClose?.();
  };

  return (
    <div class="space-y-8">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <Mail class="h-8 w-8 text-primary" />
          <h2 class="text-3xl font-bold tracking-tight">{t(msg`Email Templates`)}</h2>
        </div>
        <Button class="gap-2" onClick={() => props.onNew?.()} disabled={isCreating()}>
          <Plus class="h-4 w-4" />
          {t(msg`New Template`)}
        </Button>
      </div>

      <Show when={editNotFound()}>
        <NotFoundState
          title={t(msg`Template not found`)}
          description={t(msg`This email template may have been deleted, or never existed.`)}
          primaryAction={{
            label: t(msg`Back to templates`),
            onClick: () => props.onClose?.(),
          }}
        />
      </Show>

      <Show when={isCreating() || editing()}>
        <EmailTemplateEditor
          template={editing()}
          onSaved={onSaved}
          onCancel={() => props.onClose?.()}
        />
      </Show>

      {/* P23/I3: el ERROR es un estado propio. En error `isLoading` es
          FALSE, asi que sin esta rama el bloque de abajo pintaba el vacio
          (o el esqueleto eterno) y le mentia al usuario. */}
      <Show when={templatesQuery.isError}>
        <QueryErrorState
          testId="email-templates-error"
          compact
          onRetry={() => void templatesQuery.refetch()}
        />
      </Show>
      <Show when={!templatesQuery.isError}>
        <Show
          when={!templatesQuery.isLoading}
          fallback={
            <div class="space-y-3">
              <For each={Array.from({ length: 4 }, () => 0)}>
                {() => <Skeleton class="h-20 w-full rounded-xl" />}
              </For>
            </div>
          }
        >
          <div class="space-y-3">
            <For each={templatesQuery.data ?? []}>
              {(tpl) => (
                <div class="flex items-center justify-between rounded-xl border border-border bg-card p-4">
                  <div class="min-w-0">
                    <div class="flex items-center gap-2">
                      <span class="truncate font-semibold">{tpl.name}</span>
                      <Badge variant="secondary">{tpl.category}</Badge>
                      <Show when={tpl.isSystem}>
                        <Badge>{t(msg`System`)}</Badge>
                      </Show>
                      <Show when={!tpl.isActive}>
                        <Badge variant="outline">{t(msg`Inactive`)}</Badge>
                      </Show>
                    </div>
                    <p class="mt-1 truncate text-sm text-muted-foreground">{tpl.subject}</p>
                    <p class="mt-0.5 text-xs text-muted-foreground">
                      {(() => {
                        const { key } = tpl;
                        const version = tpl.currentVersion;
                        return t(msg`Key: ${key} · v${version}`);
                      })()}
                    </p>
                  </div>
                  <div class="flex shrink-0 items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      class="gap-1"
                      onClick={() => props.onEdit?.(tpl.id)}
                    >
                      <Pencil class="h-4 w-4" />
                      {t(msg`Edit`)}
                    </Button>
                    <Show when={!tpl.isSystem}>
                      <Button
                        variant="ghost"
                        size="sm"
                        class="gap-1 text-destructive"
                        disabled={deleteMutation.isPending}
                        onClick={() => handleDelete(tpl)}
                      >
                        <Trash2 class="h-4 w-4" />
                        {t(msg`Delete`)}
                      </Button>
                    </Show>
                  </div>
                </div>
              )}
            </For>
          </div>
        </Show>
      </Show>
    </div>
  );
}
